import { useEffect, useMemo, useState } from 'react'
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'
import { BlueButton, Button2 } from '../buttons/Buttons'

export default function RouteErrorBoundary() {
    const error = useRouteError()
    const navigate = useNavigate()
	const [copied, setCopied] = useState(false)

    function serializeError(err: unknown): string {
        try {
            if (isRouteErrorResponse(err)) {
                return JSON.stringify({
                    type: 'RouteErrorResponse',
                    status: err.status,
                    statusText: err.statusText,
                    data: err.data,
                }, null, 2)
            }
            if (err instanceof Error) {
                return JSON.stringify({
                    type: err.name || 'Error',
                    message: err.message,
                    stack: err.stack,
                }, null, 2)
            }
            return JSON.stringify(err, null, 2)
        } catch {
            return String(err)
        }
    }

	const serialized = useMemo(() => serializeError(error), [error])

	async function copyDetails() {
		try {
			await navigator.clipboard.writeText(serialized)
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		} catch {
			// ignore copy errors
		}
	}

    useEffect(() => {
        const payload: Record<string, unknown> = {}
        if (isRouteErrorResponse(error)) {
            payload.status = error.status
            payload.statusText = error.statusText
            payload.data = error.data
        } else if (error instanceof Error) {
            payload.message = error.message
            payload.stack = error.stack
        } else {
            payload.value = error
        }
    }, [error])

    function navigateHome() {
        navigate('/select-program')
    }

    const title = isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : error instanceof Error
            ? error.message
            : 'Something went wrong'

    return (
		<div className="flex flex-col items-center justify-center h-dvh gap-4 p-6 text-center">
            <div className="text-xl font-semibold">{title}</div>
			<div className="text-sm text-gray-600 max-w-prose">
                We hit an unexpected error while loading this page. Please press F12 to open
                the console and screenshot the error. Then contact richy@synswi.com
            </div>
			<div className="w-full max-w-full md:max-w-3xl">
				<div className="flex justify-end mb-2">
					<Button2 onClick={copyDetails}>{copied ? 'Copied' : 'Copy details'}</Button2>
				</div>
				<pre className="w-full text-left text-xs font-mono bg-gray-50 border rounded p-3 overflow-x-auto overflow-y-auto max-h-96">
					{serialized}
				</pre>
			</div>
            <div className="flex items-center gap-3">
                <Button2 onClick={() => navigate(-1)}>
                    Go back
                </Button2>
                <BlueButton onClick={navigateHome}>Home</BlueButton>
            </div>
        </div>
    )
}

